import React from 'react';
import PropTypes from 'prop-types';
import { Field, getIn } from 'formik';
import Select from 'react-select';
import { FormFeedback } from 'reactstrap';
import { startCase } from 'lodash';
import classnames from 'classnames';
import {
  getFieldError,
  processValidate,
  getValidity,
} from 'infrastructure/form';
import variables from 'scss/1-settings/colors.scss';
import './SelectField.scss';

const reactSelectTheme = theme => ({
  ...theme,
  borderRadius: '0.2rem',
  borderColor: 'border-color',
  colors: {
    ...theme.colors,
    primary: variables.success,
    primary75: variables['success-75'],
    primary50: variables['success-50'],
    primary25: variables['success-25'],
    danger: variables.danger,
    dangerLight: variables['danger-lighter'],
  },
});

const findValue = (options, field, isMulti, defaultValue) => {
  const hasNoField =
    !field || typeof field.value === 'undefined' || field.value === null;

  if (hasNoField) {
    if (!defaultValue) return null;

    return (options || []).find(x => x.value === defaultValue);
  }

  if (!isMulti) {
    return (options || []).find(x => x.value === field.value);
  }

  return (options || []).filter(x => field.value.indexOf(x.value) > -1);
};

const renderSelect = ({
  form,
  field,
  options,
  isMulti,
  onChange,
  defaultValue,
  hasAttemptedSubmit,
  publishRequiredOnly,
  ...props
}) => {
  const { touched = {} } = form;
  const isTouched = getIn(touched, field.name);
  const error = getFieldError(form, field.name);
  const validity = getValidity(isTouched, error, hasAttemptedSubmit);

  return (
    <div className="form__form-group-input-wrap--error-below">
      <Select
        {...props}
        classNamePrefix="rs"
        defaultValue={findValue(options, field, isMulti, defaultValue)}
        isMulti={isMulti}
        theme={reactSelectTheme}
        className={classnames({
          'is-invalid': validity.invalid,
          'is-valid': validity.valid,
        })}
        onChange={option => {
          const value = isMulti ? option.map(x => x.value) : option.value;
          form.setFieldValue(field.name, value);
          form.setFieldTouched(field.name, true);
          onChange(value);
        }}
        onBlur={() => form.setFieldTouched(field.name, true)}
        options={options}
        value={findValue(options, field, isMulti, defaultValue)}
        openMenuOnFocus
      />
      {(isTouched || hasAttemptedSubmit) && error && (
        <FormFeedback className="validation form__form-group-error" tag="small">
          {error}
        </FormFeedback>
      )}
    </div>
  );
};

const SelectField = props => {
  const { validate, label, name, publishRequiredOnly, ...fieldProps } = props;

  return (
    <Field
      {...fieldProps}
      component={renderSelect}
      name={name}
      validate={processValidate(label || startCase(name), validate, {
        ...fieldProps,
        publishRequiredOnly,
      })}
    />
  );
};
SelectField.propTypes = {
  validate: PropTypes.arrayOf(PropTypes.shape({})),
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  publishRequiredOnly: PropTypes.bool,
};
SelectField.defaultProps = {
  validate: [],
  label: '',
  onChange: () => {},
  publishRequiredOnly: false,
};

export default SelectField;
